import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { HEADER_NAV } from "../../lib/data";
import Logo from "./Logo";

const ACTIVITY_PATHS = ["/activities", "/initiatives", "/elder-care", "/projects", "/gallery", "/news"];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const { pathname } = useLocation();

  const isActive = (item) => {
    if (item.to === "/") return pathname === "/";
    if (item.children) return ACTIVITY_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
    return pathname === item.to || pathname.startsWith(item.to + "/");
  };

  const base = "px-4 py-2 rounded-sm text-sm transition-colors duration-300";
  const cls = (active) => `${base} ${active ? "bg-maroon text-ivory" : "text-ink-muted hover:bg-maroon/5 hover:text-maroon"}`;

  return (
    <header className="sticky top-0 z-50 border-b border-maroon/10 bg-ivory/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 lg:px-10">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link" onClick={() => setOpen(false)}>
          <Logo variant="header" />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {HEADER_NAV.map((item) =>
            item.children ? (
              <div key={item.to} className="group relative">
                <NavLink to={item.to} data-testid="nav-our-activities"
                  className={`inline-flex items-center gap-1 ${cls(isActive(item))}`}>
                  {item.label}
                  <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
                </NavLink>
                <div className="invisible absolute left-0 top-full translate-y-1 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="min-w-[210px] overflow-hidden rounded-sm border border-gold/30 bg-ivory shadow-[0_10px_30px_rgba(88,24,31,0.12)]" data-testid="activities-dropdown">
                    {item.children.map((c) => (
                      <NavLink key={c.to} to={c.to} data-testid={`nav-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
                        className={({ isActive: a }) => `block px-5 py-3 text-sm transition-colors duration-200 ${a ? "bg-maroon text-ivory" : "text-ink-muted hover:bg-maroon/5 hover:text-maroon"}`}>
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={item.to} to={item.to} end={item.to === "/"}
                data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={cls(isActive(item))}>
                {item.label}
              </NavLink>
            )
          )}
          <Link to="/support" data-testid="nav-support-cta"
            className="ml-2 border border-maroon bg-maroon px-5 py-2.5 text-xs uppercase tracking-wide text-ivory transition-colors duration-300 hover:bg-maroon-dark">
            Support Our Mission
          </Link>
        </nav>

        <button className="text-maroon lg:hidden" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-maroon/10 bg-ivory lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col px-5 py-3">
            {HEADER_NAV.map((item) =>
              item.children ? (
                <div key={item.to} className="border-b border-maroon/5">
                  <div className="flex items-center justify-between">
                    <NavLink to={item.to} onClick={() => setOpen(false)}
                      className={`flex-1 py-3 text-sm ${isActive(item) ? "font-semibold text-maroon" : "text-ink-muted"}`}>
                      {item.label}
                    </NavLink>
                    <button onClick={() => setSubOpen((s) => !s)} data-testid="mobile-activities-toggle" aria-label="Toggle Our Activities" className="p-2 text-maroon">
                      <ChevronDown size={18} className={`transition-transform duration-300 ${subOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {subOpen && (
                    <div className="pb-2 pl-4" data-testid="mobile-activities-submenu">
                      {item.children.map((c) => (
                        <NavLink key={c.to} to={c.to} onClick={() => setOpen(false)}
                          className={({ isActive: a }) => `block py-2.5 text-sm ${a ? "font-semibold text-maroon" : "text-ink-muted"}`}>
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} end={item.to === "/"} onClick={() => setOpen(false)}
                  className={({ isActive: a }) => `border-b border-maroon/5 py-3 text-sm ${a ? "font-semibold text-maroon" : "text-ink-muted"}`}>
                  {item.label}
                </NavLink>
              )
            )}
            <Link to="/support" onClick={() => setOpen(false)} className="mt-4 bg-maroon px-5 py-3 text-center text-xs uppercase tracking-wide text-ivory">
              Support Our Mission
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
